import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Grid, Typography} from '@mui/material';

import {DataComparisonBar} from '../../../../../components/DataComparisonBar/DataComparisonBar';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors, orangeColors, semantic} from '../../../../../layouts/theme/designSystem';
import {WorkforceProfileInferredCulturalAnswer} from '../../types/WorkforceProfileTypes';

const DEFAULT_SCALE_MAX = 6;

export interface Props {
  answers: WorkforceProfileInferredCulturalAnswer[];
}

export const WorkforceProfileInferredCulturalAnswerCards = ({answers}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage.detail.inferredCultural'});
  const currentLanguage = useContext(LanguageContext);

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
      {answers.map((answer) => {
        const methodItem = answer.methodItem;
        const title = methodItem?.title[currentLanguage] ?? answer.key;
        const rationale = answer.rationale?.[currentLanguage] ?? answer.rationale?.en ?? null;
        const scaleMax = methodItem?.scaleConfiguration.maxValue ?? DEFAULT_SCALE_MAX;

        return (
          <Box
            key={answer.key}
            sx={{
              border: `1px solid ${orangeColors[3]}`,
              borderRadius: '12px',
              p: 2,
              bgcolor: orangeColors[5],
            }}
          >
            <Typography variant='body2' sx={{fontWeight: 600, color: greenColors[700], textAlign: 'center', mb: 1}}>
              {title}
            </Typography>

            {methodItem && (
              <>
                <DataComparisonBar
                  indicators={[{value: answer.value, color: semantic.info.fg, height: '28px'}]}
                  bubbleAmount={scaleMax}
                  bubbleSize={28}
                  barColor={orangeColors[4]}
                />
                <Grid container justifyContent='space-between' sx={{mt: 1}}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant='caption' color='text.secondary' sx={{lineHeight: 1.4}}>
                      {methodItem.scaleConfiguration.labelLeft[currentLanguage]}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{lineHeight: 1.4, textAlign: {xs: 'left', sm: 'right'}}}
                    >
                      {methodItem.scaleConfiguration.labelRight[currentLanguage]}
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}

            {!methodItem && (
              <Typography variant='body2' sx={{fontWeight: 600, color: semantic.info.fg, textAlign: 'center'}}>
                {t('valueLabel', {value: answer.value, max: scaleMax})}
              </Typography>
            )}

            {rationale && (
              <Box sx={{mt: 1.5}}>
                <Typography
                  variant='caption'
                  sx={{fontWeight: 600, color: semantic.info.fg, display: 'block', mb: 0.5}}
                >
                  {t('rationaleLabel')}
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{lineHeight: 1.6}}>
                  {rationale}
                </Typography>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
